import { useEffect, useMemo, useState } from 'react';

type IntersectionObserverOptions = IntersectionObserverInit & {
    freezeOnceVisible?: boolean;
};

const basicOptions: IntersectionObserverOptions = {
    threshold: 0.1,
    root: null,
    rootMargin: '0%',
    freezeOnceVisible: false
};

export const useIntersectionObserver = <T extends Element>(options: IntersectionObserverOptions = basicOptions) => {
    const { freezeOnceVisible, root, rootMargin, threshold } = options;

    const [element, setElement] = useState<T | null>(null);
    const [isVisible, setIsVisible] = useState(false);

    const observerOptions = useMemo(
        () => ({ root, rootMargin, threshold }),
        [root, rootMargin, JSON.stringify(threshold)]
    );

    useEffect(() => {
        if (!element) {
            return;
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsVisible(true);

                if (freezeOnceVisible) {
                    observer.disconnect();
                }
            } else if (!freezeOnceVisible) {
                setIsVisible(false);
            }
        }, observerOptions);

        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, [element, freezeOnceVisible, observerOptions]);

    return { ref: setElement, isVisible };
};
